import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Button } from '../ui/Primitives';
import "./GreetingSection.css";


function GreetingSection({ disabled, draftSaveStatus, greeting, onDraftChange }) {
  const [generating, setGenerating] = useState(false);
  const [suggestion, setSuggestion] = useState('');
  const [generationError, setGenerationError] = useState('');
  const requestRef = useRef(null);
  useEffect(() => {
    if (disabled) {
      requestRef.current?.cancel();
      requestRef.current = null;
      setGenerating(false);
      setSuggestion('');
      setGenerationError('');
    }
    return () => {
      requestRef.current?.cancel();
      requestRef.current = null;
    };
  }, [disabled]);

  async function generateGreeting() {
    if (disabled || requestRef.current) return;
    const source = axios.CancelToken.source();
    requestRef.current = source;
    setGenerating(true);
    setGenerationError('');
    setSuggestion('');
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_HOST}/v2/receptionist/greeting/recommend`,
        {},
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('jwt_authorization')}` },
          cancelToken: source.token,
        },
      );
      if (requestRef.current !== source) return;
      const text = response.data?.greeting;
      if (typeof text !== 'string' || !text.trim() || text.length > 1000) {
        throw new Error('Invalid greeting');
      }
      setSuggestion(text.trim());
    } catch (error) {
      if (requestRef.current !== source || axios.isCancel(error)) return;
      const status = error.response?.status;
      setGenerationError(status === 401 ? 'Please sign in again to generate a greeting.'
        : status === 403 ? 'Administrator access is required.'
          : error.response?.data?.error?.code === 'BUSINESS_NAME_REQUIRED'
            ? 'Add your business name before generating a greeting.'
            : 'Could not generate a greeting. Please try again.');
    } finally {
      if (requestRef.current === source) {
        requestRef.current = null;
        setGenerating(false);
      }
    }
  }

  function applySuggestion() {
    if (disabled || generating || !suggestion) return;
    onDraftChange({ greeting: suggestion });
    setSuggestion('');
  }

  const placeholderText = `How would you like to greet your callers?`;
  const isSaving = ['pending', 'saving'].includes(draftSaveStatus);

  const saveStatus = draftSaveStatus === 'error'
    ? 'Draft not saved'
    : isSaving
      ? 'Saving draft…'
      : 'Draft saved automatically';

  return (
    <section className="customize-ai-section greeting-section">
      <div className="receptionist-setting-heading">
        <span className="receptionist-setting-icon" aria-hidden="true">“</span>
        <div><h2>Caller greeting</h2><p>The first thing every caller hears.</p></div>
        <Button className="greeting-generate-button" variant="secondary" size="sm"
          disabled={disabled} loading={generating} loadingLabel="Generating…" onClick={generateGreeting}>
          <span aria-hidden="true">✦</span> Generate greeting
        </Button>
      </div>
      <label className='greeting-text-container' htmlFor="greeting-text">
      <span>Greeting script</span>
      <input
        data-say-id='ai-greeting'
        id='greeting-text'
        aria-label="Greeting script"
        className="greeting-text"
        placeholder={placeholderText}
        value={greeting}
        onChange={event => onDraftChange({ greeting: event.target.value })}
        disabled={disabled}
      />
      <small className={`${draftSaveStatus === 'error' ? 'receptionist-draft-error' : ''} ${isSaving ? 'greeting-save-status busy' : 'greeting-save-status'}`.trim()}>
        {isSaving ? <i aria-hidden="true" /> : null}{saveStatus}
      </small>
      </label>
      {generationError ? <p className="greeting-generation-error" role="alert">{generationError}</p> : null}
      {suggestion ? <div className="greeting-suggestion" role="region" aria-label="Suggested greeting" aria-live="polite">
        <span className="greeting-suggestion-label">Suggested greeting</span>
        <p>{suggestion}</p>
        <div className="greeting-suggestion-actions">
          <Button size="sm" disabled={disabled || generating} onClick={applySuggestion}>Use greeting</Button>
          <Button size="sm" variant="secondary" onClick={() => setSuggestion('')}>Cancel</Button>
        </div>
      </div> : null}
    </section>
  );
}

export default GreetingSection
